import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { RootState } from '../types';
import { UserInfo } from '@/types';
import { getStudentInfo, getStudentUnreadMessages } from '@/services/ei/student/api';
import { DossierStatusInfo, InsuredInformation } from '@/services/ei/student/types';

export interface AppState {
	logged: boolean;
	user: UserInfo;
	code: number;
	dossier: DossierStatusInfo | null;
	insuredInformation: InsuredInformation | null;
	notificationNumber: string;
}

const getDefaultState = (): AppState => {
	return {
		logged: false,
		user: {},
		code: 0,
		dossier: null,
		insuredInformation: null,
		notificationNumber: ''
	};
};

const state: AppState = getDefaultState();

const actions: ActionTree<AppState, RootState> = {
	async updateNotificationNumber({ commit }) {
		let notificationNumber = '';
		try {
			const response = await getStudentUnreadMessages();
			notificationNumber = response.data.unreadMessages.toString();
			if (notificationNumber === '0') {
				notificationNumber = '';
			}
		} catch (error) {
			notificationNumber = '?';
		}
		commit('UPDATE_NOTIFICATION', notificationNumber || '');
	},
	updateUser({ commit }, user: UserInfo) {
		commit('UPDATE_USER', user);
	},
	updateDossier({ commit }, dossier: DossierStatusInfo) {
		commit('UPDATE_DOSSIER', dossier);
	},
	updateLogged({ commit }, logged: boolean) {
		commit('UPDATE_LOGGED', logged);
	},
	updateUserEmail({ commit }, email: string) {
		commit('UPDATE_USER_EMAIL', email);
	},
	updateAuthCode({ commit }, code: number) {
		commit('UPDATE_AUTH_CODE', code);
	},
	async invalidStudentInfo({  dispatch }) {
		const resStudent = await getStudentInfo();
		dispatch('registerState/updateStudentInfo', resStudent.data, { root: true });
	},
	async login({ commit, dispatch }, token) {
		sessionStorage.setItem('token', token);
		try {
			const resStudent = await getStudentInfo();
			dispatch('registerState/updateStudentInfo', resStudent.data, { root: true });
			commit('UPDATE_USER', {
				firstname: resStudent.data.firstname,
				lastname: resStudent.data.lastname
			});
			commit('UPDATE_DOSSIER', resStudent.data.dossier);
			commit('UPDATE_NIA', resStudent.data.insuredInformation);
			commit('UPDATE_ID', resStudent.data.id);
			commit('UPDATE_LOGGED', true);
			return true;
		} catch {
			dispatch('appState/logout', undefined, { root: true });
			return false;
		}
	},
	logout({ commit, dispatch }) {
		sessionStorage.removeItem('token');
		commit('LOGOUT');
		dispatch('registerState/clearStore', undefined, { root: true });
	}
};

const mutations: MutationTree<AppState> = {
	UPDATE_LOGGED(state, logged: boolean) {
		state.logged = logged;
	},
	UPDATE_NOTIFICATION(state, notificationNumber: string) {
		state.notificationNumber = notificationNumber;
	},
	UPDATE_USER(state, user: UserInfo) {
		if (!user) {
			state.user = {};
			return;
		}
		state.user = { ...state.user, ...user };
	},
	UPDATE_DOSSIER(state, dossier?: DossierStatusInfo) {
		if (!dossier) {
			state.dossier = null;
			return;
		}
		state.dossier = dossier;
	},
	UPDATE_USER_EMAIL(state, email: string) {
		state.user.email = email;
	},
	UPDATE_AUTH_CODE(state, code: number) {
		state.code = code;
	},
	UPDATE_NIA(state, insuredInformation: InsuredInformation) {
		state.insuredInformation = insuredInformation;
	},
	UPDATE_ID(state, id: string) {
		state.user.id = id;
	},
	LOGOUT(state) {
		Object.assign(state, getDefaultState());
	}
};

const getters: GetterTree<AppState, RootState> = {
	logged: state => state.logged
};

const appSate: Module<AppState, RootState> = {
	namespaced: true,
	state,
	actions,
	mutations,
	getters
};

export default appSate;
